import { plural, t } from '@lingui/core/macro'

type Args = Record<string, unknown>

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null

const count = (v: unknown): number | null =>
  Array.isArray(v) ? v.length : null

const keyCount = (v: unknown): number | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? Object.keys(v).length : null

const quote = (v: string) => `“${v.length > 60 ? `${v.slice(0, 60)}…` : v}”`

const searchTagsSentence = (args: Args, done: boolean): string => {
  const q = str(args.query)
  if (!q) return done ? t`Searched tags` : t`Searching tags`
  const query = quote(q)
  return done ? t`Searched tags for ${query}` : t`Searching tags for ${query}`
}

const checkCoverageSentence = (args: Args, done: boolean): string => {
  const n = count(args.tag_ids)
  if (!n) return done ? t`Checked data coverage` : t`Checking data coverage`
  return done
    ? plural(n, {
        one: 'Checked data coverage on # tag',
        other: 'Checked data coverage on # tags',
      })
    : plural(n, {
        one: 'Checking data coverage on # tag',
        other: 'Checking data coverage on # tags',
      })
}

const pullDataSentence = (args: Args, done: boolean): string => {
  const n = keyCount(args.roles)
  const set = str(args.dataset)
  const dataset = set ? quote(set) : null
  if (n && dataset) {
    return done
      ? plural(n, {
          one: `Pulled # tag into ${dataset}`,
          other: `Pulled # tags into ${dataset}`,
        })
      : plural(n, {
          one: `Pulling # tag into ${dataset}`,
          other: `Pulling # tags into ${dataset}`,
        })
  }
  if (n) {
    return done
      ? plural(n, { one: 'Pulled # tag', other: 'Pulled # tags' })
      : plural(n, { one: 'Pulling # tag', other: 'Pulling # tags' })
  }
  if (dataset) {
    return done
      ? t`Pulled data into ${dataset}`
      : t`Pulling data into ${dataset}`
  }
  return done ? t`Pulled data` : t`Pulling data`
}

const computeKpiSentence = (args: Args, done: boolean): string => {
  const raw = str(args.name)
  if (!raw) return done ? t`Computed the KPI` : t`Computing the KPI`
  const name = quote(raw)
  return done ? t`Computed the KPI ${name}` : t`Computing the KPI ${name}`
}

const fitSubject = (args: Args, done: boolean): string => {
  const raw = str(args.kpi)
  const n = count(args.drivers)
  const kpi = raw ? quote(raw) : null
  if (kpi && n) {
    return done
      ? plural(n, {
          one: `Fitted ${kpi} against # driver`,
          other: `Fitted ${kpi} against # drivers`,
        })
      : plural(n, {
          one: `Fitting ${kpi} against # driver`,
          other: `Fitting ${kpi} against # drivers`,
        })
  }
  if (kpi) return done ? t`Fitted ${kpi}` : t`Fitting ${kpi}`
  if (n) {
    return done
      ? plural(n, {
          one: 'Fitted the model against # driver',
          other: 'Fitted the model against # drivers',
        })
      : plural(n, {
          one: 'Fitting the model against # driver',
          other: 'Fitting the model against # drivers',
        })
  }
  return done ? t`Fitted the model` : t`Fitting the model`
}

const fitFieldModelSentence = (args: Args, done: boolean): string => {
  const subject = fitSubject(args, done)
  const days = args.smoothing_days
  if (typeof days === 'number' && days > 1) {
    return t`${subject}, smoothed over ${days} days`
  }
  return subject
}

const saveModelSentence = (args: Args, done: boolean): string => {
  const raw = str(args.name)
  if (!raw) return done ? t`Saved the model` : t`Saving the model`
  const name = quote(raw)
  return done ? t`Saved the model as ${name}` : t`Saving the model as ${name}`
}

const searchDocsSentence = (args: Args, done: boolean): string => {
  const q = str(args.query)
  if (!q) return done ? t`Searched the drawings` : t`Searching the drawings`
  const query = quote(q)
  return done
    ? t`Searched the drawings for ${query}`
    : t`Searching the drawings for ${query}`
}

const searchInDocumentSentence = (args: Args, done: boolean): string => {
  const q = str(args.query)
  if (!q) return done ? t`Looked inside a drawing` : t`Looking inside a drawing`
  const query = quote(q)
  return done
    ? t`Looked inside a drawing for ${query}`
    : t`Looking inside a drawing for ${query}`
}

const documentInstrumentsSentence = (args: Args, done: boolean): string => {
  const page = args.page
  if (typeof page === 'number') {
    return done
      ? t`Read the instruments on a drawing page ${page}`
      : t`Reading the instruments on a drawing page ${page}`
  }
  return done
    ? t`Read the instruments on a drawing`
    : t`Reading the instruments on a drawing`
}

const crossCheckTagsSentence = (args: Args, done: boolean): string => {
  const n = count(args.columns)
  if (!n) {
    return done
      ? t`Cross-checked tags against each other`
      : t`Cross-checking tags against each other`
  }
  return done
    ? plural(n, {
        one: 'Cross-checked # tag against each other',
        other: 'Cross-checked # tags against each other',
      })
    : plural(n, {
        one: 'Cross-checking # tag against each other',
        other: 'Cross-checking # tags against each other',
      })
}

const detectStepsSentence = (args: Args, done: boolean): string => {
  const raw = str(args.kpi)
  const n = count(args.dates)
  const kpi = raw ? quote(raw) : null
  if (n && kpi) {
    return done
      ? plural(n, {
          one: `Tested # date for step changes in ${kpi}`,
          other: `Tested # dates for step changes in ${kpi}`,
        })
      : plural(n, {
          one: `Testing # date for step changes in ${kpi}`,
          other: `Testing # dates for step changes in ${kpi}`,
        })
  }
  if (n) {
    return done
      ? plural(n, {
          one: 'Tested # date for step changes',
          other: 'Tested # dates for step changes',
        })
      : plural(n, {
          one: 'Testing # date for step changes',
          other: 'Testing # dates for step changes',
        })
  }
  if (kpi) {
    return done
      ? t`Tested for step changes in ${kpi}`
      : t`Testing for step changes in ${kpi}`
  }
  return done ? t`Tested for step changes` : t`Testing for step changes`
}

const loadModelSentence = (args: Args, done: boolean): string => {
  const id = str(args.entry_id)
  if (!id) return done ? t`Loaded a saved model` : t`Loading a saved model`
  const shortId = id.slice(-8)
  return done
    ? t`Loaded a saved model (${shortId})`
    : t`Loading a saved model (${shortId})`
}

/**
 * Renders a tool call as a transcript headline. Tense carries the state:
 * "Searching tags…" while it runs, "Searched tags" once the result is in.
 */
export const toolSentence = (
  tool: string,
  args: Args,
  done: boolean
): string => {
  switch (tool) {
    case 'search_tags':
      return searchTagsSentence(args, done)
    case 'check_coverage':
      return checkCoverageSentence(args, done)
    case 'pull_data':
      return pullDataSentence(args, done)
    case 'compute_kpi':
      return computeKpiSentence(args, done)
    case 'fit_field_model':
      return fitFieldModelSentence(args, done)
    case 'save_model':
      return saveModelSentence(args, done)
    case 'search_docs':
      return searchDocsSentence(args, done)
    case 'search_in_document':
      return searchInDocumentSentence(args, done)
    case 'document_instruments':
      return documentInstrumentsSentence(args, done)
    case 'cross_check_tags':
      return crossCheckTagsSentence(args, done)
    case 'detect_steps':
      return detectStepsSentence(args, done)
    case 'list_models':
      return done ? t`Listed the saved models` : t`Listing the saved models`
    case 'load_model':
      return loadModelSentence(args, done)
    default: {
      const readable = tool.replace(/_/g, ' ')
      return done ? t`Ran ${readable}` : t`Running ${readable}`
    }
  }
}
